import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, ThemeProvider } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { Button } from "@src/appV2/redesign/components/Button";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { StickyScrollPageHeader } from "../../components/StickyScrollPageHeader";
import { PageContent } from "../../WorkerShifts/components/PageContent";
import { useSupportShifts } from "../api/useSupportShifts";
import { getCancelledShiftDetailsPath, getShiftDetailsPath } from "../paths";
import { ShiftStatusFilter, type ShiftStatusFilterType } from "../types";
import { GuidedSupportEmptyState } from "./EmptyState";
import { GuidedSupportLoadingState } from "./LoadingState";
import { GuidedSupportShiftList } from "./ShiftList";
import { GuidedSupportTabs } from "./Tabs";

/**
 * GuidedSupportPage is the main page for the interactive support flow.
 * Workers can select a shift from upcoming or past shifts and proceed
 * to report an issue or get help.
 */
const GUIDED_SUPPORT_TAB_KEY = "guided-support-tab";

export function GuidedSupportPage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const location = useLocation();
  const worker = useDefinedWorker();

  const [statusFilter, setStatusFilter] = useState<ShiftStatusFilterType>(() => {
    const saved = sessionStorage.getItem(GUIDED_SUPPORT_TAB_KEY);
    return saved === ShiftStatusFilter.UPCOMING
      ? ShiftStatusFilter.UPCOMING
      : ShiftStatusFilter.PAST;
  });

  const showCancelledShifts = useCbhFlag(CbhFeatureFlag.SHOW_CANCELLED_SHIFTS_IN_SUPPORT_FLOW, {
    defaultValue: false,
  });

  // Log page view on mount
  useEffect(() => {
    logEvent(APP_V2_USER_EVENTS.GUIDED_SUPPORT_PAGE_VIEWED);
  }, []);

  const { pastShifts, upcomingShifts, isLoading, isError, refetch } = useSupportShifts({
    workerId: worker.userId,
    showCancelledShifts,
  });

  const shifts = statusFilter === ShiftStatusFilter.UPCOMING ? upcomingShifts : pastShifts;

  const handleShiftSelect = useCallback(
    (shiftId: string) => {
      const selectedShift = shifts.find((shift) => shift.id === shiftId);
      const isCancelled = selectedShift?.deleted === true;
      logEvent(APP_V2_USER_EVENTS.GUIDED_SUPPORT_SHIFT_SELECTED, { shiftId, isCancelled });
      if (isCancelled) {
        history.push(getCancelledShiftDetailsPath(shiftId));
      } else {
        history.push(getShiftDetailsPath(shiftId));
      }
    },
    [history, shifts]
  );

  const handleStatusFilterChange = useCallback((newStatus: ShiftStatusFilterType) => {
    logEvent(APP_V2_USER_EVENTS.GUIDED_SUPPORT_TAB_CHANGED, { tab: newStatus });
    setStatusFilter(newStatus);
    sessionStorage.setItem(GUIDED_SUPPORT_TAB_KEY, newStatus);
  }, []);

  const handleClose = useCallback(() => {
    sessionStorage.removeItem(GUIDED_SUPPORT_TAB_KEY);
    // Check if we have navigation history to go back to.
    // history.length <= 2 means we're at the first page (e.g., fresh deep link entry).
    // location.key being undefined means the page was accessed directly (not via navigation).
    if (isDefined(location.key) && history.length > 2) {
      history.goBack();
    } else {
      // No history to go back to (fresh deep link) - navigate to home
      // This prevents unexpected behavior when users deep-link directly to this page
      history.replace("/home/account");
    }
  }, [history, location.key]);

  const { showErrorToast } = useToast();

  const handleSomethingElse = useCallback(async () => {
    logEvent(APP_V2_USER_EVENTS.GUIDED_SUPPORT_SOMETHING_ELSE_TAPPED);
    try {
      await openZendeskMessaging();
    } catch {
      showErrorToast("Unable to open support chat. Please try again.");
    }
  }, [showErrorToast]);

  function renderShiftContent(): ReactNode {
    if (isLoading) {
      return <GuidedSupportLoadingState />;
    }

    if (isError) {
      return (
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "12.5rem",
            gap: theme.spacing(2),
          })}
        >
          <Box sx={{ color: "text.secondary" }}>Unable to load shifts. Please try again.</Box>
          <Button variant="outlined" onClick={refetch}>
            Retry
          </Button>
        </Box>
      );
    }

    if (shifts.length === 0) {
      return <GuidedSupportEmptyState statusFilter={statusFilter} />;
    }

    return <GuidedSupportShiftList shifts={shifts} onShiftSelect={handleShiftSelect} />;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          title="Is this about a shift?"
          scrollContainerRef={scrollRef}
          navigateBack={handleClose}
          headerVariant="tertiary"
          rightAction={
            <IconButton
              aria-label="Close"
              variant="outlined"
              iconType="close"
              size="medium"
              onClick={handleClose}
            />
          }
        />

        {/* Scrollable Content */}
        <PageContent
          ref={scrollRef}
          sx={{
            flex: 1,
          }}
        >
          <GuidedSupportTabs
            statusFilter={statusFilter}
            upcomingCount={upcomingShifts.length}
            pastCount={pastShifts.length}
            onStatusFilterChange={handleStatusFilterChange}
          />
          {renderShiftContent()}
        </PageContent>

        {/* Footer Button */}
        <PageFooterWrapper>
          <LoadingButton
            fullWidth
            isLoading={false}
            size="large"
            variant="outlined"
            onClick={handleSomethingElse}
          >
            It&apos;s something else
          </LoadingButton>
        </PageFooterWrapper>
      </PageWrapper>
    </ThemeProvider>
  );
}
